import { supabase } from './supabase';

export interface OpportunityLineItem {
  id: string;
  productName: string;
  description: string;
  quantity: number;
  unitPrice: number;
  totalPrice: number;
}

export interface OpportunityCustomer {
  accountId: string;
  name: string;
  email: string;
  phone: string;
  projectStreet: string;
  projectPostcode: string;
  projectCity: string;
}

export interface OpportunityPartner {
  accountId: string | null;
  name: string;
  contactName: string;
  email: string;
  phone: string;
  sentAt: string | null;
}

export interface OpportunityFinancials {
  amount: number;
  totalInclBtw: number;
  totalBtwAmount: number;
  commissionAmount: number;
  cincoShare: number;
  payoutStatus: string;
  invoiced: boolean;
  gefactureerd: boolean;
}

export interface OpportunityTracking {
  leadSource: string;
  primarySource: string;
  utmSource: string;
  utmMedium: string;
  utmCampaign: string;
  utmTerm: string;
  utmContent: string;
  gclid: string;
  webSource: string;
}

export interface OpportunityDetail {
  id: string;
  name: string;
  opportunityNumber: string;
  stage: string;
  type: string;
  channel: string;
  recordType: string;
  executionStatus: string;
  reasonLost: string;
  brand: string;
  closeDate: string;
  plannedExecutionDate: string;
  createdDate: string;
  lastModifiedDate: string;
  ownerName: string;
  description: string;
  notesExecution: string;
  notesOffer: string;
  partnerRemarks: string;
  customer: OpportunityCustomer;
  partner: OpportunityPartner;
  financials: OpportunityFinancials;
  tracking: OpportunityTracking;
  lineItems: OpportunityLineItem[];
  workOrderId: string | null;
  workOrderName: string;
}

export interface OpportunityListItem {
  id: string;
  opportunityNumber: string;
  name: string;
  customerName: string;
  customerCity: string;
  partnerName: string;
  stage: string;
  type: string;
  channel: string;
  amount: number;
  totalInclBtw: number;
  createdDate: string;
  hasWorkOrder: boolean;
  primaryProduct: string;
  primaryQuantity: number;
  lineItemCount: number;
}

async function authHeaders(): Promise<Record<string, string>> {
  const { data: { session } } = await supabase.auth.getSession();
  const h: Record<string, string> = { 'Content-Type': 'application/json' };
  if (session?.access_token) h['Authorization'] = `Bearer ${session.access_token}`;
  return h;
}

async function parseOrThrow<T = unknown>(response: Response): Promise<T> {
  if (!response.ok) {
    const err = await response.json().catch(() => ({ error: 'Request failed' }));
    const error = new Error(err.error || err.message || 'Request failed') as Error & { status?: number };
    error.status = response.status;
    throw error;
  }
  return response.json() as Promise<T>;
}

export async function fetchOpportunityList(): Promise<OpportunityListItem[]> {
  const headers = await authHeaders();
  const response = await fetch('/api/opportunities/list', { headers });
  const payload = await parseOrThrow<{ opportunities?: OpportunityListItem[] }>(response);
  return Array.isArray(payload.opportunities) ? payload.opportunities : [];
}

export async function searchOpportunities(
  query: string,
  signal?: AbortSignal
): Promise<OpportunityListItem[]> {
  const headers = await authHeaders();
  const response = await fetch(
    `/api/opportunities/search?q=${encodeURIComponent(query)}`,
    { headers, signal }
  );
  const payload = await parseOrThrow<{ opportunities?: OpportunityListItem[] }>(response);
  return Array.isArray(payload.opportunities) ? payload.opportunities : [];
}

export async function fetchOpportunityDetail(id: string): Promise<OpportunityDetail> {
  const headers = await authHeaders();
  const response = await fetch(`/api/opportunities/detail?id=${encodeURIComponent(id)}`, { headers });
  return parseOrThrow<OpportunityDetail>(response);
}
